/**
 * Approvals inbox — proposals from schedulers and autopilot that need
 * operator sign-off before execution.
 *
 * Deployed agents and schedulers can queue actions here instead of
 * executing them directly, creating an audit trail and letting the
 * operator batch-review + approve/dismiss before they take effect.
 * Each approval is run via runActionWithUndo on approval (best-effort
 * undo, not persisted for future recovery).
 *
 * Unlike the chat flow where undo is stored on the proposal, approval
 * undo is session-local: once the session reloads, the action is
 * committed and undoing becomes a second manual action. This keeps
 * the approval record simple (just the action + outcome).
 */

import { db, nowIso, uuid } from '../db/store';
import { logActivity } from '../db/activity';
import { runActionWithUndo, summarizeAction } from './actions';
import type { AgentAction } from './types';
import type { ApprovalItem } from '../db/types';

/**
 * Queue an action for approval. Stores it in the approvals collection
 * with status 'pending' until the operator approves or dismisses.
 */
export async function queueApproval(
  action: AgentAction,
  source: 'scheduler' | 'autopilot' | 'chat',
  note?: string,
  /**
   * Optional proposer-supplied explanation: a one-sentence `rationale` plus
   * concrete `evidence` lines (e.g. "Follow-up 21 days overdue"). Rendered
   * as the expandable "Why?" section in the approval inbox. Omitted fields
   * are simply absent from the record — older callers/proposals unchanged.
   */
  why?: { rationale?: string; evidence?: string[] },
): Promise<ApprovalItem> {
  const ts = nowIso();
  // Stamp the human-readable label now, while we have the executor layer in
  // scope. Lets a server-side consumer (Slack notifier) render the proposal
  // without re-deriving it. summarizeAction is pure.
  const sum = summarizeAction(action);
  const record: ApprovalItem = {
    id: uuid(),
    action,
    source,
    note,
    summary: sum,
    ...(why?.rationale ? { rationale: why.rationale } : {}),
    ...(why?.evidence && why.evidence.length > 0 ? { evidence: why.evidence } : {}),
    status: 'pending',
    createdAt: ts,
    resolvedAt: null,
  };
  await db.add('approvals', record);
  // Record the proposal in the governance activity log so the full lifecycle
  // (proposed → approved/rejected → executed) is auditable. Best-effort.
  await logActivity({
    event: 'proposed',
    summary: [sum.verb, sum.detail].filter(Boolean).join(' — '),
    source,
    kind: action.kind,
    subjectId: record.id,
  }).catch(() => undefined);
  return record;
}

/**
 * List all approvals, typically filtered by status. Returns newest-first
 * (most recent approvals at the top of the list).
 */
export async function listApprovals(status?: 'pending' | 'approved' | 'dismissed'): Promise<ApprovalItem[]> {
  const all = await db.list('approvals');
  const filtered = status ? all.filter((a) => a.status === status) : all;
  return filtered.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
}

/**
 * Get a single approval by id.
 */
export async function getApproval(id: string): Promise<ApprovalItem | null> {
  return db.get('approvals', id);
}

/**
 * Resolve an approval (approve or dismiss). On 'approved', runs the action
 * via runActionWithUndo (best-effort undo, session-local). On 'dismissed',
 * just marks the status. Sets resolvedAt timestamp.
 */
export async function resolveApproval(
  id: string,
  decision: 'approved' | 'dismissed',
  actor: string = 'operator',
): Promise<ApprovalItem> {
  const ts = nowIso();
  const existing = await db.get('approvals', id);
  if (!existing) throw new Error(`No approval ${id}`);

  // Idempotency fence: an approval resolves exactly once. If it's already
  // resolved (approved OR dismissed), a second resolve — double-click, retried
  // worker, stale tab — must NOT re-run the action, re-append to the ledger,
  // or flip the recorded decision. Return the existing record as a no-op.
  if (existing.status !== 'pending') return existing;

  // Every governed decision is recorded to the append-only activity log so the
  // governance story is a real control, not a UI pattern. Logging is
  // best-effort — it must never block or break the decision it records.
  const sum = existing.summary ?? summarizeAction(existing.action);
  const summary = [sum.verb, sum.detail].filter(Boolean).join(' — ');
  const base = { summary, source: existing.source, kind: existing.action.kind, subjectId: id };

  if (decision === 'approved') {
    // Record the approval intent up front, then run the action. If execution
    // fails we still have the 'approved' entry; only a real commit logs
    // 'executed', so the log never claims work that didn't happen.
    await logActivity({ event: 'approved', ...base }).catch(() => undefined);
    try {
      // The tamper-evident Decision Ledger is fed inside runActionWithUndo
      // (the single execution seam): a successful execute appends a
      // kind:'action' entry attributed to the approving `actor`.
      await runActionWithUndo(existing.action, actor);
      await logActivity({ event: 'executed', ...base }).catch(() => undefined);
    } catch (err) {
      // Approval intent recorded even though execution failed. The error
      // would be shown in the UI; the operator can manually retry or undo.
      // eslint-disable-next-line no-console
      console.warn(`[Approvals] Execution of ${id} failed:`, err);
    }
  } else {
    await logActivity({ event: 'rejected', ...base }).catch(() => undefined);
  }

  return db.update('approvals', id, {
    status: decision,
    resolvedAt: ts,
  });
}

/**
 * Subscribe to approvals changes. Returns an unsubscribe function.
 */
export function subscribeApprovals(fn: (approvals: ApprovalItem[]) => void): () => void {
  return db.subscribe('approvals', fn);
}
